import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useQuery, type QueryClient } from '@tanstack/vue-query';
import { getLeaderboard } from '../leaderboard.api';
import { QueryKey } from '@/api/common/types/query-key.enum';
import { useAuthState } from '@/state/user';
import { firebaseAuth } from '@/api/auth/auth.api';
import type { GetLeaderboardParams } from '../types/leaderboard';

export const LEADERBOARD_PREVIEW_LIMIT = 3;
export const LEADERBOARD_FULL_LIMIT = 50;
/** Aligns with the backend in-memory ranking cache without blocking freshness after invalidation. */
export const LEADERBOARD_STALE_TIME_MS = 30_000;

export const GLOBAL_LEADERBOARD_PREVIEW_PARAMS: GetLeaderboardParams = {
  scopeType: 'global',
  limit: LEADERBOARD_PREVIEW_LIMIT,
};

export const leaderboardQueryKey = (params: GetLeaderboardParams) =>
  [
    QueryKey.GetLeaderboard,
    params.scopeType || 'global',
    params.scopeId || '',
    params.limit || LEADERBOARD_FULL_LIMIT,
  ] as const;

export const leaderboardFullParams = (params: GetLeaderboardParams): GetLeaderboardParams => ({
  scopeType: params.scopeType || 'global',
  ...(params.scopeId ? { scopeId: params.scopeId } : {}),
  limit: LEADERBOARD_FULL_LIMIT,
});

export const fetchLeaderboard = (params: GetLeaderboardParams) => getLeaderboard(params);

const prefetchLeaderboard = (queryClient: QueryClient, params: GetLeaderboardParams) => {
  if (params.scopeType === 'group' && !params.scopeId) return;
  return queryClient.prefetchQuery({
    queryKey: leaderboardQueryKey(params),
    queryFn: () => fetchLeaderboard(params),
    staleTime: LEADERBOARD_STALE_TIME_MS,
  });
};

/** Fire-and-forget. Must not be awaited by splash / startup. */
export const prefetchMatchingFullLeaderboard = (queryClient: QueryClient, previewParams: GetLeaderboardParams) => {
  const fullParams = leaderboardFullParams(previewParams);
  const previewKey = leaderboardQueryKey({
    ...previewParams,
    limit: previewParams.limit || LEADERBOARD_PREVIEW_LIMIT,
  });
  const fullKey = leaderboardQueryKey(fullParams);
  if (previewKey.join('|') === fullKey.join('|')) return;
  void prefetchLeaderboard(queryClient, fullParams);
};

/** Fire-and-forget. Must not be awaited by splash / startup. */
export const prefetchGlobalLeaderboardPreview = (queryClient: QueryClient) => {
  const { isBackendUserProvisioned } = useAuthState();
  // `/api/leaderboard` does not self-provision, so firing it on Firebase auth
  // alone races signup's `auth/register` and 401s the user straight back out.
  if (!firebaseAuth.currentUser?.uid || !isBackendUserProvisioned.value) return;
  void prefetchLeaderboard(queryClient, GLOBAL_LEADERBOARD_PREVIEW_PARAMS)?.then(
    () => prefetchMatchingFullLeaderboard(queryClient, GLOBAL_LEADERBOARD_PREVIEW_PARAMS),
    () => undefined
  );
};

export function useLeaderboard(params: MaybeRefOrGetter<GetLeaderboardParams>) {
  const { userAuth, isBackendUserProvisioned } = useAuthState();
  const hasAuth = computed(
    () => isBackendUserProvisioned.value && (!!userAuth.value?.uid || !!firebaseAuth.currentUser?.uid)
  );
  const resolved = computed(() => toValue(params));
  const enabled = computed(() => {
    if (!hasAuth.value) return false;
    if (resolved.value.scopeType === 'group') return !!resolved.value.scopeId;
    return true;
  });

  return useQuery({
    queryKey: computed(() => leaderboardQueryKey(resolved.value)),
    queryFn: () => fetchLeaderboard(resolved.value),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: LEADERBOARD_STALE_TIME_MS,
  });
}
